import React, { Fragment } from "react";

import img13 from "../../../images/service/Cryptocurrency Exchange Development/1.png";
import img14 from "../../../images/service/Cryptocurrency Exchange Development/2.png";
import img15 from "../../../images/service/Cryptocurrency Exchange Development/3.png";

import sIcon1 from "../../../images/icon/service-icon01.svg";
import sIcon2 from "../../../images/icon/service-icon02.svg";
import sIcon3 from "../../../images/icon/service-icon03.svg";
import sIcon4 from "../../../images/icon/service-icon04.svg";
import sIcon5 from "../../../images/icon/service-icon05.svg";
import sIcon6 from "../../../images/icon/service-icon06.svg";

import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Scrollbar from "../../../components/scrollbar/scrollbar";
import Footer from "../../../components/footer/Footer";

import HeroSection from "../../../components/PageSection/HeroSection";
import TheorySection from "../../../components/PageSection/Theory";
import ServiceCardsSection from "../../../components/PageSection/ServiceCardSection";
import Benefit from "../../../components/PageSection/BenefitSection";
import KeyFeaturesSection from "../../../components/PageSection/KeyFeature";
import WhyChooseUsSection from "../../../components/PageSection/WhyChooseUsSection";
import FaqSection from "../../../components/PageSection/FAQsection";
import FinalCenteredCTA from "../../../components/PageSection/FinalCTA";

// --------------------------------------------------
// DATA
// --------------------------------------------------

const serviceCards = [
  {
    icon: sIcon1,
    title: "Centralized Exchange Development",
    content:
      "High-performance centralized crypto exchanges with advanced trading engines, order books, and liquidity management.",
  },
  {
    icon: sIcon2,
    title: "Decentralized Exchange Development",
    content:
      "Secure non-custodial DEX platforms powered by smart contracts and automated market makers.",
  },
  {
    icon: sIcon3,
    title: "AI-Powered Exchange Solutions",
    content:
      "AI-driven trading insights, fraud detection, user behavior analysis, and predictive market analytics.",
  },
];

const benefits = [
  {
    title: "High Transaction Throughput",
    description:
      "Optimized matching engines ensure fast trade execution even during peak volumes.",
    icon: sIcon1,
  },
  {
    title: "Advanced Security",
    description:
      "Multi-layer security architecture including encryption, cold wallets, and DDoS protection.",
    icon: sIcon2,
  },
  {
    title: "AI-Driven Intelligence",
    description:
      "Machine learning models improve trading accuracy, risk detection, and user personalization.",
    icon: sIcon3,
  },
  {
    title: "Scalable Architecture",
    description:
      "Built to scale seamlessly with growing users, assets, and trading volume.",
    icon: sIcon4,
  },
  {
    title: "Regulatory Readiness",
    description:
      "Supports compliance requirements such as KYC, AML, and audit trails.",
    icon: sIcon5,
  },
  {
    title: "Global Market Reach",
    description:
      "Multi-currency, multi-language, and multi-region support for global users.",
    icon: sIcon6,
  },
];

const faqs = [
  {
    q: "What types of crypto exchanges can you develop?",
    a: "We build centralized, decentralized, hybrid, P2P, and AI-powered cryptocurrency exchanges.",
  },
  {
    q: "What role does AI play in crypto exchanges?",
    a: "AI enhances fraud detection, trade analytics, user personalization, and predictive insights.",
  },
  {
    q: "Is liquidity management included?",
    a: "Yes. We integrate liquidity providers, market-making bots, and pool management strategies.",
  },
  {
    q: "Do you provide post-launch support?",
    a: "Yes. We offer maintenance, upgrades, security patches, and performance optimization.",
  },
];

// --------------------------------------------------
// PAGE COMPONENT
// --------------------------------------------------

export default function CryptocurrencyExchangeDevelopment() {
  return (
    <Fragment>
      <Header />

      <PageTitle
        pageTitle="Cryptocurrency Exchange Development"
        pagesub="Build secure, scalable, and AI-powered crypto trading platforms"
      />

      {/* ================= HERO ================= */}
      <HeroSection
        title={
          <>
            Next-Generation{" "}
            <span className="gradient-text">Crypto Exchange Development</span>
          </>
        }
        subtitle={`We design and develop high-performance cryptocurrency exchanges with
advanced trading engines, AI-powered intelligence, and enterprise-grade security.
Launch centralized, decentralized, or hybrid exchanges with confidence.`}
        buttonText="Build Your Exchange"
        buttonLink="/contact"
        image={img13}
        imageAlt="Cryptocurrency Exchange Development"
      />

      {/* ================= THEORY 1 ================= */}
      <TheorySection
        title="Cryptocurrency Exchange Development Explained"
        image={img14}
        paragraphs={[
          "Cryptocurrency exchange development involves building digital trading platforms where users can buy, sell, and trade cryptocurrencies securely and efficiently. These platforms act as the backbone of the crypto economy, enabling liquidity, price discovery, and seamless asset exchange across global markets.",
          "Modern crypto exchanges are no longer limited to simple trade execution. They incorporate advanced matching engines, real-time analytics, security layers, and scalable infrastructure to handle millions of transactions while maintaining performance and reliability.",
        ]}
      />

      {/* ================= THEORY 2 ================= */}
      <TheorySection
        title="AI-Powered Crypto Exchanges"
        image={img15}
        paragraphs={[
          "AI-powered crypto exchanges leverage machine learning and data intelligence to enhance trading efficiency and security. AI models analyze market trends, detect anomalies, prevent fraudulent activities, and provide personalized insights to traders.",
          "By integrating artificial intelligence, exchanges can offer smarter order execution, automated risk management, and improved user experience—making platforms more resilient, adaptive, and competitive in fast-moving crypto markets.",
        ]}
      />

      {/* ================= SERVICES ================= */}
      <ServiceCardsSection
        title="Our Cryptocurrency Exchange Development Services"
        cards={serviceCards}
      />

      {/* ================= BENEFITS ================= */}
      <Benefit
        title="Benefits of Crypto Exchange Development"
        benefits={benefits}
      />

      {/* ================= KEY FEATURES ================= */}
      <KeyFeaturesSection
        title="Key Features of Our Crypto Exchange Platforms"
        subtitle="Engineered for speed, security, and intelligent trading."
        features={[
          {
            icon: sIcon1,
            title: "High-Speed Matching Engine",
            description:
              "Ultra-low latency trade execution with real-time order matching.",
          },
          {
            icon: sIcon2,
            title: "AI Fraud Detection",
            description:
              "Machine-learning models detect suspicious activities and prevent fraud.",
          },
          {
            icon: sIcon3,
            title: "Multi-Asset Trading",
            description:
              "Support for crypto-crypto, crypto-fiat, and derivative trading pairs.",
          },
          {
            icon: sIcon4,
            title: "Advanced Wallet Management",
            description:
              "Hot and cold wallet integration with multi-signature security.",
          },
          {
            icon: sIcon5,
            title: "KYC & AML Integration",
            description:
              "Compliance-ready onboarding and transaction monitoring.",
          },
          {
            icon: sIcon6,
            title: "Scalable Cloud Infrastructure",
            description:
              "Designed to handle exponential growth and high trading volumes.",
          },
        ]}
      />

      {/* ================= WHY CHOOSE US ================= */}
      <WhyChooseUsSection
        items={[
          "Proven Exchange Development Expertise",
          "AI-Driven Trading & Security Solutions",
          "End-to-End Exchange Launch Support",
        ]}
      />

      {/* ================= FAQ ================= */}
      <FaqSection faqs={faqs} />

      {/* ================= CTA ================= */}
      <FinalCenteredCTA />

      <Footer />
      <Scrollbar />
    </Fragment>
  );
}
